import React, { memo, useEffect } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { InlineEditContainer } from 'transfix-ui/components/InlineEditContainer'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Typography } from 'transfix-ui/core'
import { IFullStop } from 'types/shipment'
import { DRIVER_IN_OUT_FORM_STATE } from 'apps/fleet-planner/hooks/useDriverInOutForm'
import { IStop } from 'apps/fleet-planner/types/loads'
import { LOAD_FIELDS } from 'apps/fleet-planner/utils/permissions'
import { ITimeFormats } from 'apps/fleet-planner/utils/userDateTimeSettings'
import { LockedTooltip } from '../LockedTooltip'
import { CheckInTimes } from './CheckInTimes'
import { CheckOutTimes } from './CheckOutTimes'
import { InOutTimes } from './InOutTimes'

interface IInOutDetailsProps {
  checkedInAt?: string | null
  checkedOutAt?: string | null
  displayTimezone?: string
  stopType: string
  sequence: number
  timeFormats: ITimeFormats
  onButtonClick?: (state: DRIVER_IN_OUT_FORM_STATE) => void
}

export const InOutDetails = memo<IInOutDetailsProps>(
  ({
    checkedInAt,
    checkedOutAt,
    displayTimezone,
    onButtonClick,
    sequence,
    stopType,
    timeFormats,
  }) => {
    return (
      <div className='load-details-row-in-out-times'>
        <CheckInTimes
          stopType={stopType}
          sequence={sequence}
          checkedInAt={checkedInAt}
          timeFormats={timeFormats}
          onButtonClick={onButtonClick}
          displayTimezone={displayTimezone}
        />
        <CheckOutTimes
          stopType={stopType}
          sequence={sequence}
          checkedOutAt={checkedOutAt}
          timeFormats={timeFormats}
          onButtonClick={onButtonClick}
          displayTimezone={displayTimezone}
        />
      </div>
    )
  }
)

interface IInOutTimesInlineEditProps {
  marketplaceStop?: IFullStop
  stop: IStop
  currentEditField?: string
  onEditModeRequested(name?: string): void
  onSubmit: () => void
  onCancel: () => void
  onStopUpdate: (stop: Partial<IStop>) => void
  loading?: boolean
  error?: string
  timeFormats: ITimeFormats
}

export const InOutTimesInlineEdit = memo<IInOutTimesInlineEditProps>(
  ({
    currentEditField,
    error,
    loading,
    marketplaceStop,
    onCancel,
    onEditModeRequested,
    onStopUpdate,
    onSubmit,
    stop,
    timeFormats,
  }) => {
    const { checkedInAt = '', checkedOutAt = '', displayTimezone, id, stopSequence, stopType } =
      stop || {}
    const { status } = marketplaceStop || {}
    const { arrivedAt, leftAt } = status || {}
    const NAME = `${LOAD_FIELDS.inOut}-${id}`
    const isEditMode = currentEditField === NAME

    useEffect(() => {
      if (!error && !loading && isEditMode) onEditModeRequested('')
    }, [error, loading, onEditModeRequested])

    return (
      <>
        <InlineEditContainer
          isLocked={!!(arrivedAt && leftAt)}
          isLoading={loading}
          lockedIcon={<LockedTooltip className='locked-icon' />}
          dataTest={`load-details-stop-in-out-times-${stopType}-${stopSequence}`}
          name={NAME}
          editModeEnabled={isEditMode}
          resetOnClickOutside={false}
          editMode={
            <Card className='pa4'>
              {error && (
                <div className='row pl0 mb4'>
                  <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />
                </div>
              )}
              <InOutTimes
                marketplaceStop={marketplaceStop}
                timeFormat={timeFormats.stopTimeInput}
                stop={stop}
                onStopChange={onStopUpdate}
                variant='load-details'
              />
              <div className='tr'>
                <div className='mt4'>
                  <Button
                    data-test={`stop-in-out-cancel-${stopType}-${stopSequence}`}
                    size='large'
                    color='tertiary'
                    onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    color='primary'
                    data-test={`stop-in-out-submit-${stopType}-${stopSequence}`}
                    size='large'
                    onClick={onSubmit}>
                    Save
                  </Button>
                </div>
              </div>
            </Card>
          }
          onEditModeRequested={onEditModeRequested}>
          <InOutDetails
            timeFormats={timeFormats}
            displayTimezone={displayTimezone}
            stopType={stopType}
            sequence={stopSequence}
            checkedInAt={arrivedAt || checkedInAt}
            checkedOutAt={leftAt || checkedOutAt}
          />
          {arrivedAt && leftAt && (
            <Typography color='textSecondary' variant='caption' component='div'>
              These are the timestamps Transfix has on file.
            </Typography>
          )}
        </InlineEditContainer>
      </>
    )
  }
)
